import { useCallback, useEffect, useRef, useState } from "react";
import api from "../../services/api";

const SCRIPT_ID = "facebook-jssdk";
const SDK_SRC = "https://connect.facebook.net/en_US/sdk.js";

// Memoizado no módulo: o script entra na página UMA vez, mesmo com vários
// componentes usando o hook.
let sdkPromise = null;

/**
 * Injeta (ou aguarda) o SDK do Facebook.
 *
 * A react-facebook-login v4 usa exatamente o mesmo script e o mesmo id, então
 * se ela já tiver injetado nós apenas esperamos o load em vez de duplicar.
 */
const loadSdk = () => {
  if (sdkPromise) return sdkPromise;

  sdkPromise = new Promise((resolve, reject) => {
    if (window.FB) {
      resolve(window.FB);
      return;
    }

    const existing = document.getElementById(SCRIPT_ID);

    if (existing) {
      existing.addEventListener("load", () => resolve(window.FB));
      existing.addEventListener("error", () =>
        reject(new Error("Falha ao carregar o SDK do Facebook"))
      );
      return;
    }

    const script = document.createElement("script");
    script.id = SCRIPT_ID;
    script.src = SDK_SRC;
    script.async = true;
    script.defer = true;
    script.crossOrigin = "anonymous";
    script.onload = () => resolve(window.FB);
    script.onerror = () =>
      reject(new Error("Falha ao carregar o SDK do Facebook"));

    document.body.appendChild(script);
  });

  return sdkPromise;
};

/**
 * Carrega o SDK do Facebook e devolve uma instância já inicializada com o
 * appId/versão do backend (GET /plugins/meta-signup/config).
 *
 * ⚠️ Conflito conhecido: a react-facebook-login v4.1.1 (usada nos botões de
 * Facebook e Instagram desta mesma tela) inicializa o `window.FB` GLOBAL com
 * `version: "9.0"`, que NÃO suporta `config_id` — parâmetro obrigatório do
 * Embedded Signup. Como `window.FB` é único na página, quem chamou `FB.init`
 * por último vence. Por isso `ensureSdk` reinicializa a cada uso, em vez de
 * inicializar só uma vez: assim o ES sempre roda na versão correta,
 * independentemente de o usuário ter clicado antes em Facebook/Instagram.
 */
const useFacebookSdk = () => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;

    const fetchConfig = async () => {
      try {
        const { data } = await api.get("/plugins/meta-signup/config");
        if (!mountedRef.current) return;
        setConfig(data);
        setError(null);
      } catch (err) {
        if (!mountedRef.current) return;
        // 503 = envs não preenchidas no backend. Não é erro de usuário:
        // o botão simplesmente não deve aparecer/funcionar.
        setError(err);
        setConfig(null);
      } finally {
        if (mountedRef.current) setLoading(false);
      }
    };

    fetchConfig();

    return () => {
      mountedRef.current = false;
    };
  }, []);

  /**
   * Garante SDK carregado e inicializado na NOSSA versão. Sempre chame
   * imediatamente antes de FB.login.
   */
  const ensureSdk = useCallback(async () => {
    if (!config?.appId) {
      throw new Error("Embedded Signup não está configurado no servidor.");
    }

    const FB = await loadSdk();

    if (!FB) {
      throw new Error("SDK do Facebook indisponível.");
    }

    // Idempotente e barato — reafirma a versão caso a react-facebook-login
    // tenha reinicializado o FB global com a v9.0.
    FB.init({
      appId: config.appId,
      version: config.graphVersion,
      cookie: true,
      xfbml: false
    });

    return FB;
  }, [config]);

  return {
    config,
    loading,
    error,
    isAvailable: !!config?.appId && !!config?.configId,
    ensureSdk
  };
};

export default useFacebookSdk;
